/**
 * Custom hook for ActionPlan Dashboard logic
 * Manages state, API calls, and business logic
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { Animated, Platform, Dimensions } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { createApiClient, apiClient, getTimezoneOffsetString } from '../../../api/apiClient';
import { setToastNotification } from '../../../store/slices/actionPlanSlice';
import { ActionPlanItem, ActionPlanPagination } from '../types';
import { FilterItem } from '../components/TableSection';
import {
  ActionPlanFilters,
  ApiActionPlan,
  CalendarEvent,
  DropDownOption,
} from '../types';
import {
  DEFAULT_FILTERS,
  FILTER_DEBOUNCE_DELAY,
  COLOR_MAPPING,
} from '../constants';
import {
  transformApiActionPlanToCalendarEvent,
  buildFilterOptions,
  mapStatusToApi,
  buildStatusUpdateRequestBody,
} from '../utils/actionPlanTransformers';
import {
  formatDateForApi,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getFirstDayOfWeek,
  getLastDayOfWeek,
} from '../utils/dateUtils';
import { formatUserName } from '../../../utils/emailTouserName';
import { setTopOpportunities as setTopOpportunitiesRedux } from "../../../store/slices/standUpSlice";
import { TopOpportunityItem } from '../../StandUp/components/TopOpportunitiesTable';
import { handleActionPlanErrorWithFriendlyMessage, ACTION_PLAN_API_NAMES } from '../../../store/utils/actionPlanErrorHandler';

interface UseActionPlanDashboardReturn {
  // State
  activeTab: 'Hybrid' | 'Table' | 'Calendar';
  actionPlanData: ActionPlanItem[];
  isLoading: boolean;
  error: string | null;
  pagination: ActionPlanPagination | null;
  currentPage: number;
  filters: ActionPlanFilters;
  filterOptions: FilterItem[];
  events: CalendarEvent[];
  calendarEventsLoading: boolean;
  calendarEventsError: string | null;
  calendarDate: Date;
  isFiltersExpanded: boolean;
  filterAnimation: Animated.Value;
  showActionPlanDetails: boolean;
  selectedActionPlanId: string | null;
  selectedActionPlanData: ActionPlanItem | null;
  isEditMode: boolean;
  isMobile: boolean;
  isTablet: boolean;

  // Handlers
  handleTabChange: (tab: 'Hybrid' | 'Table' | 'Calendar') => void;
  handleFilterChange: (key: string, value: DropDownOption | DropDownOption[] | null) => void;
  handleStatusChange: (itemId: string, newStatus: string) => Promise<void>;
  handleDeleteSuccess: (deletedItemId: string) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleRetry: () => void;
  handleCalendarRetry: () => void;
  handleCalendarNavigationChange: (newDate: Date, viewMode: 'weekly' | 'monthly') => void;
  toggleFiltersExpansion: () => void;
  handleEditActionPlan: (item: ActionPlanItem) => void;
  handleViewActionPlanDetails: (item: ActionPlanItem) => void;
  handleCalendarEventViewDetails: (event: CalendarEvent) => void;
  handleBackFromDetails: () => void;
  resetFilters: () => Promise<void>;
  getResponsiveItemsPerRow: (itemsPerRow: number) => number;
  handleCalendarViewModeChange: (mode: 'weekly' | 'monthly') => void;

  // Calendar event handlers
  handleDateSelect: (date: Date) => void;
  handleEventPress: (event: CalendarEvent) => void;
  handleEventRemove: (eventId: string) => void;
  handleEventDetailsPress: (event: CalendarEvent) => void;
  handleShowAllEvents: (date: Date, eventsForDate: CalendarEvent[]) => void;

  // Setters for parent component to update list
  setActionPlanData: (data: ActionPlanItem[] | ((prev: ActionPlanItem[]) => ActionPlanItem[])) => void;
  setSelectedActionPlanData: (data: ActionPlanItem | null) => void;
  setFilterDataset: (data: ActionPlanItem[] | ((prev: ActionPlanItem[]) => ActionPlanItem[])) => void;
}

export const useActionPlanDashboard = (
  selectedLocation: { id: string; displayText: string } | null,
  options?: { isStandup?: boolean; skipCalendarAndFilters?: boolean; sessionId?: string; top_opportunity?: number; is_temp?: boolean; topOpportunitiesData?: TopOpportunityItem[]; excludeIsTempParam?: boolean; }
): UseActionPlanDashboardReturn => {
  const { isStandup = false, skipCalendarAndFilters = false, sessionId, top_opportunity, is_temp, topOpportunitiesData = [], excludeIsTempParam = false } = options || {};
  
  // Redux dispatch
  const dispatch = useDispatch();

  // Get logged-in user's GPID, role ID, role name, and email from Redux
  const loggedInUserGpid = useSelector((state: RootState) => state.appData?.userInfo?.GPID);
  const loggedInUserRoleId = useSelector((state: RootState) => state.appData?.userInfo?.roleId);
  const loggedInUserRoleName = useSelector((state: RootState) => state.appData?.userInfo?.roleName);
  const loggedInUserEmail = useSelector((state: RootState) => state.appData?.userInfo?.email ?? state.auth?.user?.username);

  // Privileged roles that can complete any action plan
  const PRIVILEGED_ROLES = ['Super User', 'COE', 'Region Leader/Division'];

  // Component state
  const [activeTab, setActiveTab] = useState<'Hybrid' | 'Table' | 'Calendar'>('Hybrid');
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [calendarViewMode, setCalendarViewMode] = useState<'weekly' | 'monthly'>('weekly');
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [calendarEventsLoading, setCalendarEventsLoading] = useState(false);
  const [calendarEventsError, setCalendarEventsError] = useState<string | null>(null);

  // Action Plans API state
  const [actionPlanData, setActionPlanData] = useState<ActionPlanItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<ActionPlanPagination | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<ActionPlanFilters>(DEFAULT_FILTERS);

  // Filter state
  const [filterOptions, setFilterOptions] = useState<FilterItem[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<ActionPlanFilters>(DEFAULT_FILTERS);
  // Separate state for full dataset used ONLY for building filter dropdowns
  const [filterDataset, setFilterDataset] = useState<ActionPlanItem[]>([]);
  const [isLoadingFilterData, setIsLoadingFilterData] = useState(false);
  
  // State for managing owner options fetched from API
  const [ownerOptions, setOwnerOptions] = useState<Array<{ id: string; displayText: string; originalValue: string }>>([]);
  
  // State for managing assignee options fetched from API
  const [assigneeOptions, setAssigneeOptions] = useState<Array<{ id: string; displayText: string; originalValue: string }>>([]);

  // Mobile filter collapse/expand state
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const filterAnimation = useMemo(() => new Animated.Value(0), []);

  // Navigation state
  const [showActionPlanDetails, setShowActionPlanDetails] = useState(false);
  const [selectedActionPlanId, setSelectedActionPlanId] = useState<string | null>(null);
  const [selectedActionPlanData, setSelectedActionPlanData] = useState<ActionPlanItem | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Mobile detection helper
  const isMobile = useMemo(() => {
    return Platform.OS === 'ios' || Platform.OS === 'android';
  }, []);

  // Check if device is iPad specifically
  const isTablet = useMemo(() => {
    const { width, height } = Dimensions.get('window');
    return Platform.OS === 'ios' && Math.min(width, height) >= 768;
  }, []);

  // Fetch owner options from users API on mount/location change
  useEffect(() => {
    console.log('🟡 Owner fetch effect triggered. selectedLocation:', selectedLocation);
    
    const fetchOwners = async () => {
      // Check if location exists with various property names
      const locationName = selectedLocation?.displayText || 
                          (selectedLocation as any)?.name || 
                          (selectedLocation as any)?.locationName;
      
      console.log('🟡 Extracted location name:', locationName);
      
      if (!locationName) {
        console.log('🔴 No location name found, skipping owner fetch');
        return;
      }
      
      try {
        const api = createApiClient();
        const apiUrl = `/common/users/users-by-role-ids?roleIds=1,2,3,4,5,6,7,8,9&location=${encodeURIComponent(locationName)}`;
        console.log('🔵 Fetching owner options from:', apiUrl);
        console.log('🔵 Full API URL:', `${api.defaults.baseURL}${apiUrl}`);
        
        const response = await apiClient(api, apiUrl, 'GET');
        console.log('🔵 Owner API Response:', response);
        
        // Extract users array from response
        const users = response?.users || [];
        console.log('🔵 Extracted users count:', users.length);
        console.log('🔵 First 3 users:', users.slice(0, 3));
        
        // Map users to dropdown options with formatted names
        const ownerDropdownOptions = [
          { id: '0', displayText: 'All', originalValue: 'All' },
          ...users.map((user: any, index: number) => {
            const displayText = formatUserName(user.UserEmail);
            return {
              id: String(index + 1),
              displayText,
              originalValue: user.GPID
            };
          })
        ];
        
        console.log('🔵 Final owner options count:', ownerDropdownOptions.length);
        console.log('🔵 First 3 owner options:', ownerDropdownOptions.slice(0, 3));
        setOwnerOptions(ownerDropdownOptions);
      } catch (err) {
        console.error('🔴 Error fetching owner options:', err);
        // Send centralized ActionPlan error so the overlay/modal can surface this failure
        try {
          handleActionPlanErrorWithFriendlyMessage(ACTION_PLAN_API_NAMES.FETCH_LIST, err, true, { location: locationName });
        } catch (handlerErr) {
          console.error('Failed to dispatch centralized ActionPlan error:', handlerErr);
        }

        // Keep UI fallback so dropdown still shows "All"
        setOwnerOptions([{ id: '0', displayText: 'All', originalValue: 'All' }]);
      }
    };
    
    fetchOwners();
  }, [selectedLocation]);

  // Fetch assignee options from users/location API on mount/location change
  useEffect(() => {
    console.log('🟡 Assignee fetch effect triggered. selectedLocation:', selectedLocation);
    
    const fetchAssignees = async () => {
      // Check if location exists with various property names
      const locationName = selectedLocation?.displayText || 
                          (selectedLocation as any)?.name || 
                          (selectedLocation as any)?.locationName;
      
      console.log('🟡 Extracted location name for assignees:', locationName);
      
      if (!locationName) {
        console.log('🔴 No location name found, skipping assignee fetch');
        return;
      }
      
      try {
        const api = createApiClient();
        const apiUrl = `/users/location/${encodeURIComponent(locationName)}`;
        console.log('🔵 Fetching assignee options from:', apiUrl);
        console.log('🔵 Full API URL:', `${api.defaults.baseURL}${apiUrl}`);
        
        const response = await apiClient(api, apiUrl, 'GET');
        console.log('🔵 Assignee API Response:', response);
        
        // Extract data array from response - handle nested data structure
        const items = response?.data?.data || response?.data || [];
        console.log('🔵 Extracted assignees count:', items.length);
        console.log('🔵 First 3 assignees:', items.slice(0, 3));
        
        // Map items to dropdown options with formatted names
        const assigneeDropdownOptions = [
          { id: '0', displayText: 'All', originalValue: 'All' },
          ...items.map((item: any, index: number) => {
            // Extract user email and GPID from API response
            const userEmail = item.UserEmail || item.userEmail || item.email;
            const gpid = item.GPID || item.gpid || item.Gpid;
            // Format name from email (e.g., "john.doe@pepsico.com" -> "John Doe")
            const displayText = userEmail ? formatUserName(userEmail) : (gpid || 'Unknown');
            
            return {
              id: String(index + 1),
              displayText,
              originalValue: gpid || ''
            };
          })
        ];
        
        console.log('🔵 Final assignee options count:', assigneeDropdownOptions.length);
        console.log('🔵 First 3 assignee options:', assigneeDropdownOptions.slice(0, 3));
        setAssigneeOptions(assigneeDropdownOptions);
      } catch (err) {
        console.error('🔴 Error fetching assignee options:', err);
        setAssigneeOptions([{ id: '0', displayText: 'All', originalValue: 'All' }]);
      }
    };
    
    fetchAssignees();
  }, [selectedLocation]);

  /**
   * Fetch Calendar Events from API
   */
  const fetchCalendarEvents = useCallback(
    async (date: Date, viewMode: 'weekly' | 'monthly' = 'monthly') => {
      try {
        setCalendarEventsLoading(true);
        setCalendarEventsError(null);

        let startDate: Date;
        let endDate: Date;

        if (viewMode === 'weekly') {
          startDate = getFirstDayOfWeek(date);
          endDate = getLastDayOfWeek(date);
        } else {
          startDate = getFirstDayOfMonth(date);
          endDate = getLastDayOfMonth(date);
        }

        const startDateStr = formatDateForApi(startDate);
        const endDateStr = formatDateForApi(endDate);

        const queryParams: ActionPlanFilters = {
          ...DEFAULT_FILTERS,
          ...appliedFilters, // Apply current filters to calendar events as well
          target_date_from: startDateStr,
          target_date_to: endDateStr,
          location: selectedLocation?.displayText || '',
          pageSize: 100,
        };

        const queryString = Object.entries(queryParams)
          .map(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
            }
            return null;
          })
          .filter((param) => param !== null)
          .join('&');

        // Add client timestamp and timezone
        const clientTimestamp = new Date().toISOString();
        const clientTimezone = getTimezoneOffsetString();
        const queryWithTimestamp = `${queryString}&clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`;

        const api = createApiClient();
        const endpoint = `/action-plans?${queryWithTimestamp}`;

        console.log('Fetching calendar events:', endpoint);
        console.log('Calendar fetch params - date:', date.toISOString(), 'viewMode:', viewMode, 'startDate:', startDateStr, 'endDate:', endDateStr);

        const response = await apiClient(api, endpoint, 'GET');

        if (response.status === 'success' && response.data) {
          const calendarEvents = response.data.map((actionPlan: ApiActionPlan) =>
            transformApiActionPlanToCalendarEvent(actionPlan)
          );
          setEvents(calendarEvents);
          console.log(`Loaded ${calendarEvents.length} calendar events for ${viewMode} view`);
          if (calendarEvents.length > 0) {
            console.log('First event date:', calendarEvents[0].date.toISOString(), 'Last event date:', calendarEvents[calendarEvents.length - 1].date.toISOString());
          }
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching calendar events:', err);
        // Dispatch centralized ActionPlan error and show modal (safe minimal context)
        try {
          const centralizedError = handleActionPlanErrorWithFriendlyMessage(
            ACTION_PLAN_API_NAMES.FETCH_LIST,
            err,
            true,
            { date: date.toISOString(), viewMode }
          );
          setCalendarEventsError(centralizedError?.message || (err instanceof Error ? err.message : 'Failed to fetch calendar events'));
        } catch (handlerErr) {
          console.error('Failed to dispatch centralized ActionPlan error for calendar events:', handlerErr);
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch calendar events';
          setCalendarEventsError(errorMessage);
        }
        // Don't clear events on error to prevent UI flash - keep previous events visible
      } finally {
        setCalendarEventsLoading(false);
      }
    },
    [selectedLocation, appliedFilters]
  );

  /**
   * Fetch ALL Action Plans for Filter Dropdowns (NO pagination)
   * This ensures dropdowns show all available options, not just current page
   */
  const fetchActionPlansForFilters = useCallback(
    async () => {
      try {
        setIsLoadingFilterData(true);
        
        // Build query params WITHOUT pageNumber and pageSize
        // Include all default filter parameters so dropdowns receive full dataset
        // We'll perform paginated fetches in case the backend enforces pagination
        // even when pageNumber/pageSize are omitted. This loop will collect all
        // pages (up to a safe limit) and merge results for the dropdown dataset.
        const pageSizeFallback = 100; // reasonable batch size
        const maxPages = 50; // safety cap to avoid infinite loops (pageSizeFallback * maxPages)
        let allItems: ActionPlanItem[] = [];
        let pageNumber = 1;
        let keepFetching = true;

        const api = createApiClient();

        while (keepFetching && pageNumber <= maxPages) {
          const apiParams: Record<string, any> = {
            source: DEFAULT_FILTERS.source,
            type: DEFAULT_FILTERS.type,
            status: DEFAULT_FILTERS.status,
            priority: DEFAULT_FILTERS.priority,
            startDate: DEFAULT_FILTERS.startDate,
            sort_by: DEFAULT_FILTERS.sort_by,
            sort_order: DEFAULT_FILTERS.sort_order,
            location: selectedLocation?.displayText || DEFAULT_FILTERS.location,
            //pageNumber,
            pageSize: pageSizeFallback,
          };
          
          // Add standup-specific parameters to match the table data
          if (isStandup) {
            apiParams.is_standup = true;
          }
          
          if (sessionId) {
            apiParams.session_id = sessionId;
          }
          
          if (top_opportunity !== undefined) {
            apiParams.top_opportunity = top_opportunity;
          }
          
          // Only include is_temp if excludeIsTempParam is not set
          if (!excludeIsTempParam && is_temp !== undefined) {
            apiParams.is_temp = is_temp;
          }

          const queryString = Object.entries(apiParams)
            .map(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
              }
              return null;
            })
            .filter((param) => param !== null)
            .join('&');

          // Add client timestamp and timezone
          const clientTimestamp = new Date().toISOString();
          const clientTimezone = getTimezoneOffsetString();
          const queryWithTimestamp = `${queryString}&clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`;

          const endpoint = `/action-plans?${queryWithTimestamp}`;
          console.log(`Fetching filter page ${pageNumber}:`, endpoint);

          const response = await apiClient(api, endpoint, 'GET');

          if (response.status === 'success' && response.data) {
            // response.data may be an array or contain nested arrays (data.items, data.data, data.results)
            const raw = response.data;
            let items: ActionPlanItem[] = [];
            if (Array.isArray(raw)) {
              items = raw;
            } else if (raw && Array.isArray(raw.data)) {
              items = raw.data;
            } else if (raw && Array.isArray(raw.items)) {
              items = raw.items;
            } else if (raw && Array.isArray(raw.results)) {
              items = raw.results;
            } else {
              items = [];
            }
            allItems = allItems.concat(items);

            // If pagination info is present, use it to decide when to stop
            if (response.pagination && typeof response.pagination.totalPages === 'number') {
              if (pageNumber >= response.pagination.totalPages) {
                keepFetching = false;
              } else {
                pageNumber += 1;
              }
            } else {
              // If no pagination info, stop when we receive fewer items than pageSize
              if (items.length < pageSizeFallback) {
                keepFetching = false;
              } else {
                pageNumber += 1;
              }
            }
          } else {
            // invalid response - stop and fallback to empty
            console.warn('Invalid response while fetching filter pages, stopping fetch.');
            keepFetching = false;
          }
        }

        setFilterDataset(allItems);
        console.log(`Loaded ${allItems.length} items for filter dropdowns (merged ${pageNumber - 1} pages)`);
        if (allItems.length > 0) {
          console.log('Sample filter item keys:', Object.keys(allItems[0]).slice(0, 20));
        }
      } catch (err) {
        console.error('Error fetching action plans for filters:', err);
        // Dispatch centralized ActionPlan error (non-critical but surfaced centrally) with minimal context
        try {
          handleActionPlanErrorWithFriendlyMessage(ACTION_PLAN_API_NAMES.FETCH_LIST, err, true, { location: selectedLocation?.displayText });
        } catch (handlerErr) {
          console.error('Failed to dispatch centralized ActionPlan error for filter fetch:', handlerErr);
        }
        // Don't set error state here as it's non-critical for filters
        setFilterDataset([]);
      } finally {
        setIsLoadingFilterData(false);
      }
    },
    [selectedLocation, isStandup, sessionId, top_opportunity, is_temp, excludeIsTempParam]
  );

  /**
   * Fetch Action Plans List from API (WITH pagination for datatable)
   */
  const fetchActionPlans = useCallback(
    async (page: number = 1, filterOverrides?: Partial<ActionPlanFilters>) => {
      try {
        setIsLoading(true);
        setError(null);
        const currentFilters = {
          ...DEFAULT_FILTERS,
          ...filters,
          ...filterOverrides,
          pageNumber: page,
        };

        // Transform filter parameters for API
        const apiParams: Record<string, any> = { ...currentFilters };
        
        // Add is_standup parameter if this is for standup flow
        if (isStandup) {
          apiParams.is_standup = true;
        }
        
        // Add session_id parameter when provided
        if (sessionId) {
          apiParams.session_id = sessionId;
        }
        
        // Add top_opportunity parameter when provided
        if (top_opportunity !== undefined) {
          apiParams.top_opportunity = top_opportunity;
        }
        
        // Add is_temp parameter when provided (unless excluded)
        if (!excludeIsTempParam && is_temp !== undefined) {
          apiParams.is_temp = is_temp;
        }
        
        // Map assignedUser filter to assignee_gpid parameter
        if (currentFilters.assignedUser && currentFilters.assignedUser !== 'All') {
          apiParams.assignee_gpid = currentFilters.assignedUser;
        }
        delete apiParams.assignedUser;
        
        // Map actionPlanOwner filter to owner_gpid parameter
        if (currentFilters.actionPlanOwner && currentFilters.actionPlanOwner !== 'All') {
          apiParams.owner_gpid = currentFilters.actionPlanOwner;
        }
        delete apiParams.actionPlanOwner;
        
        // Map targetDate filter to both target_date_to and target_date_from parameters
        if (currentFilters.targetDate && currentFilters.targetDate !== 'All' && currentFilters.targetDate !== '') {
          // targetDate is already in ISO format from originalValue
          apiParams.target_date_to = currentFilters.targetDate;
          apiParams.target_date_from = currentFilters.targetDate;
        }
        delete apiParams.targetDate;
        
        // Exclude parameters that should not be sent to API
        const excludedParams = ['start_date_from', 'start_date_to'];

        const queryString = Object.entries(apiParams)
          .map(([key, value]) => {
            if (value !== undefined && value !== null && !excludedParams.includes(key)) {
              return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
            }
            return null;
          })
          .filter((param) => param !== null)
          .join('&');

        // Add client timestamp and timezone
        const clientTimestamp = new Date().toISOString();
        const clientTimezone = getTimezoneOffsetString();
        const queryWithTimestamp = `${queryString}&clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`;

        const api = createApiClient();
        // Only add is_temp=false if excludeIsTempParam is not set
        const endpoint = excludeIsTempParam 
          ? `/action-plans?${queryWithTimestamp}` 
          : `/action-plans?${queryWithTimestamp}&is_temp=false`;

        console.log('Fetching action plans:', endpoint);

        const response = await apiClient(api, endpoint, 'GET');

        if (response.status === 'success' && response.data) {
          setActionPlanData(response.data);
          setPagination(response.pagination || null);
          setCurrentPage(page);
          setAppliedFilters(currentFilters);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching action plans:', err);
        // Dispatch centralized ActionPlan error and show modal
        try {
          const centralizedError = handleActionPlanErrorWithFriendlyMessage(
            ACTION_PLAN_API_NAMES.FETCH_LIST,
            err,
            true,
            { page, filters }
          );
          setError(centralizedError?.message || (err instanceof Error ? err.message : 'Failed to fetch action plans'));
        } catch (handlerErr) {
          console.error('Failed to dispatch centralized ActionPlan error for list fetch:', handlerErr);
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch action plans';
          setError(errorMessage);
        }
        // Clear data to avoid showing stale content
        setActionPlanData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [filters]
  );

  /**
   * Handle filter changes
   */
  const handleFilterChange = useCallback((key: string, value: DropDownOption | DropDownOption[] | null | Date) => {
    // Handle date picker values (Date objects)
    if (value instanceof Date) {
      // Convert to YYYY-MM-DD format using local date to avoid timezone issues
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      const isoDate = `${year}-${month}-${day}`;
      console.log(`Filter change - key: ${key}, date value:`, isoDate);
      
      setCurrentPage(1);
      setFilters(prev => {
        const newFilters = {
          ...prev,
          [key]: isoDate,
          pageNumber: 1,
        };
        console.log(`Filter '${key}' changed to date: ${isoDate}`);
        console.log('New filters state:', newFilters);
        return newFilters;
      });
      return;
    }
    
    // Handle null date (clear date picker)
    if (value === null && key === 'targetDate') {
      console.log(`Filter change - key: ${key}, clearing date`);
      
      setCurrentPage(1);
      setFilters(prev => {
        const newFilters = {
          ...prev,
          [key]: 'All',
          pageNumber: 1,
        };
        console.log(`Filter '${key}' cleared`);
        console.log('New filters state:', newFilters);
        return newFilters;
      });
      return;
    }
    
    // Use originalValue for API calls, fallback to displayText if not available
    const selectedOption = Array.isArray(value) ? value[0] : value;
    const filterValue = selectedOption?.originalValue || selectedOption?.displayText || 'All';
    const displayValue = selectedOption?.displayText || 'All';
    
    console.log(`Filter change - key: ${key}, selectedOption:`, selectedOption);
    console.log(`Filter change - filterValue: ${filterValue}, displayValue: ${displayValue}`);
    
    setCurrentPage(1);
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [key]: filterValue,
        pageNumber: 1,
      };
      console.log(`Filter '${key}' changed to '${filterValue}' (display: '${displayValue}')`);
      console.log('New filters state:', newFilters);
      return newFilters;
    });
  }, []);

  /**
   * Handle status change for action plan
   */
  const handleStatusChange = useCallback(
    async (itemId: string, newStatus: string) => {
      try {
        let itemToUpdate = actionPlanData.find(item => String(item.ID) === itemId);
         if (isStandup) {
           itemToUpdate = topOpportunitiesData.find(
             (item) => String(item.ID) === itemId,
           );
         }  
        if (!itemToUpdate) {
          throw new Error('Action plan not found');
        }

        setActionPlanData(prevData =>
          prevData.map(item =>
            String(item.ID) === itemId
              ? { ...item, STATUS: newStatus }
              : item
          )
        );

        // Update calendar events with new status and color (completely recreate array like delete)
        setEvents(prevEvents => {
          console.log('Before status change - events:', prevEvents.length, prevEvents.find(e => String(e.id) === itemId));
          
          // Completely recreate the events array (same as delete approach)
          const updatedEvents = prevEvents.reduce((acc, event) => {
            if (String(event.id) === itemId) {
              // Determine color based on new status and priority
              let newColor = COLOR_MAPPING.DEFAULT;
              let isDone = false;

              if (newStatus.toLowerCase() === 'completed') {
                isDone = true;
                newColor = COLOR_MAPPING.COMPLETED;
              }

              // Create new event object - keep existing event, only update color and status
              const newEvent = {
                ...event,
                color: newColor,
                isDone: isDone,
                actionPlanData: {
                  ...event.actionPlanData,
                  STATUS: newStatus // Update status in nested data too
                },
                lastUpdated: Date.now()
              };
              acc.push(newEvent);
            } else {
              // Keep unchanged events as-is
              acc.push(event);
            }
            return acc;
          }, [] as CalendarEvent[]);
          
          console.log('After status change - events:', updatedEvents.length, updatedEvents.find(e => String(e.id) === itemId));
          return updatedEvents; // Return completely new array
        });

        const apiStatus = mapStatusToApi(newStatus);
        const api = createApiClient();

        // If user selected Completed, call the dedicated complete endpoint (POST /action-plans/{id}/complete)
        if (apiStatus === 'completed' || apiStatus === 'complete') {
          // Use logged-in user's GPID, role ID, and email for complete API
          const completePayload = {
            assignee_gpid: loggedInUserGpid || '',
            assignee_role_id: loggedInUserRoleId || '',
            assignee_email: loggedInUserEmail || '',
          };
          
          // Check if user is a privileged role (Super User, COE, Region Leader/Division)
          // These roles can complete any action plan, so we need to auto-accept first if status is pending
          const isPrivilegedRole = PRIVILEGED_ROLES.includes(loggedInUserRoleName || '');
          const assignmentStatus = (itemToUpdate.ASSIGNMENT_STATUS || '').toLowerCase();
          
          if (isPrivilegedRole && assignmentStatus === 'pending') {
            console.log('Privileged role detected with pending status, auto-accepting first...');
            // Use the logged-in user's info for accept API (privileged user has permission to accept)
            const acceptPayload = {
              assignee_gpid: loggedInUserGpid || '',
              assignee_role_id: loggedInUserRoleId || '',
              assignee_email: loggedInUserEmail || '',
            };
            
            const acceptResponse = await apiClient(
              api,
              `/action-plans/${itemToUpdate.ID}/accept`,
              'POST',
              acceptPayload
            );
            
            if (acceptResponse?.status && acceptResponse.status !== 'success') {
              throw new Error(acceptResponse.message || 'Failed to accept action plan');
            }
            console.log('Action plan auto-accepted successfully, now completing...');
          }
          
          console.log('Calling complete endpoint for action plan', itemToUpdate.ID, 'payload:', completePayload);

          const completeResponse = await apiClient(
            api,
            `/action-plans/${itemToUpdate.ID}/complete`,
            'POST',
            completePayload
          );

          if (completeResponse?.status && completeResponse.status !== 'success') {
            throw new Error(completeResponse.message || 'Failed to complete action plan');
          }

          // Update local copy to completed
          setActionPlanData(prevData =>
            prevData.map(item =>
              String(item.ID) === itemId ? { ...item, STATUS: 'completed' } : item
            )
          );
          console.log('Action plan completed successfully:', itemId);
              if (isStandup) {
                // Update Redux store immediately for instant UI feedback
                const updatedTopOpportunities = topOpportunitiesData.map(
                  (item) =>
                    String(item.ID) === itemId
                      ? { ...item, STATUS: "completed" }
                      : item,
                );
                dispatch(setTopOpportunitiesRedux(updatedTopOpportunities));
              } 
        } else {
          const requestBody = buildStatusUpdateRequestBody(itemToUpdate, apiStatus);

          const response = await apiClient(
            api,
            `/action-plans/${itemToUpdate.ID}`,
            'PUT',
            requestBody
          );

          if (response.status !== 'success') {
            throw new Error(response.message || 'Failed to update action plan');
          }
        }
      } catch (error: any) {
        console.error('Failed to update status:', error);

        // Revert the optimistic update
        setActionPlanData(prevData =>
          prevData.map(item =>
            String(item.ID) === itemId
              ? { ...item, STATUS: actionPlanData.find(originalItem => String(originalItem.ID) === itemId)?.STATUS || item.STATUS }
              : item
          )
        );

        // Also revert calendar events color changes (completely recreate array)
        const originalItem = actionPlanData.find(item => String(item.ID) === itemId);
        if (originalItem) {
          setEvents(prevEvents => {
            console.log('Reverting calendar events for item:', itemId);
            
            return prevEvents.reduce((acc, event) => {
              if (String(event.id) === itemId) {
                // Restore original color based on original status and priority
                let originalColor = COLOR_MAPPING.DEFAULT;
                let wasCompleted = false;

                if (originalItem.STATUS?.toLowerCase() === 'completed') {
                  wasCompleted = true;
                  originalColor = COLOR_MAPPING.COMPLETED;
                }

                console.log(`Reverting calendar event ${event.id} to original color ${originalColor}`);

                // Create new reverted event object - keep existing event, only update color and status
                const revertedEvent = {
                  ...event,
                  color: originalColor,
                  isDone: wasCompleted,
                  actionPlanData: {
                    ...event.actionPlanData,
                    STATUS: originalItem.STATUS // Revert status in nested data too
                  },
                  lastUpdated: Date.now()
                };
                acc.push(revertedEvent);
              } else {
                acc.push(event);
              }
              return acc;
            }, [] as CalendarEvent[]);
          });
        }

        // Show error toast notification
        const errorMessage = error?.response?.data?.details || error?.message || 'Failed to update status';
        dispatch(setToastNotification({
          header: 'Error',
          subtext: errorMessage,
          variant: 'error',
          position: 'top',
          duration: 4000,
        }));

        throw error;
      }
    },
    [actionPlanData, dispatch, loggedInUserGpid, loggedInUserRoleId, loggedInUserRoleName, loggedInUserEmail, PRIVILEGED_ROLES]
  );

  /**
   * Handle successful deletion of action plan
   */
  const handleDeleteSuccess = useCallback(
    (deletedItemId: string) => {
      // Remove the deleted item from actionPlanData
      setActionPlanData(prevData => 
        prevData.filter(item => String(item.ID) !== deletedItemId)
      );

      // Also remove from calendar events if present
      setEvents(prevEvents => 
        prevEvents.filter(event => String(event.id) !== deletedItemId)
      );

      // Remove deleted item from the full filter dataset used to build filter dropdowns
      setFilterDataset(prevFilterDataset => {
        const newDataset = prevFilterDataset.filter(item => String(item.ID) !== deletedItemId);
        try {
          // Rebuild filter options immediately so the UI dropdowns reflect the deletion
          const options = buildFilterOptions(newDataset, filters, ownerOptions, assigneeOptions);
          setFilterOptions(options);
        } catch (e) {
          console.warn('Failed to rebuild filter options after delete:', e);
        }
        return newDataset;
      });

      console.log('Action plan deleted successfully:', deletedItemId);
    },
    [filters, ownerOptions, assigneeOptions]
  );

  /**
   * Reset all filters to default state but keep current location
   */
  const resetFilters = useCallback(async () => {
    try {
      // Reset filters but preserve the current location
      const resetFiltersWithLocation = {
        ...DEFAULT_FILTERS,
        location: selectedLocation?.displayText || '',
      };
      setFilters(resetFiltersWithLocation);
      setCurrentPage(1);
      await fetchActionPlans(1, resetFiltersWithLocation);
      // Also refresh filter dataset to ensure date picker and other filters reset properly
      await fetchActionPlansForFilters();
      console.log('Filters reset to default values with current location:', selectedLocation?.displayText);
    } catch (error) {
      console.error('Failed to reset filters:', error);
    }
  }, [fetchActionPlans, fetchActionPlansForFilters, selectedLocation?.displayText]);

  // Update filter options when filter dataset, owner options, or assignee options change
  useEffect(() => {
    const options = buildFilterOptions(filterDataset, filters, ownerOptions, assigneeOptions);
    setFilterOptions(options);
  }, [filterDataset, filters, ownerOptions, assigneeOptions]);

  // Auto-apply filters with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const { pageNumber: _, ...filtersWithoutPagination } = filters;
      const { pageNumber: __, ...appliedFiltersWithoutPagination } = appliedFilters;

      const filtersChanged = JSON.stringify(filtersWithoutPagination) !== JSON.stringify(appliedFiltersWithoutPagination);
      if (filtersChanged) {
        setCurrentPage(1);
        fetchActionPlans(1, { ...filters, pageNumber: 1 });
      }
    }, FILTER_DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [filters, appliedFilters, fetchActionPlans]);

  // Initial data load and update filters when location changes
  useEffect(() => {
    if (selectedLocation?.displayText) {
      setFilters(prev => ({
        ...prev,
        location: selectedLocation.displayText,
      }));
      // Fetch full dataset for filter dropdowns whenever location changes
      // Always call this even in standup context to populate filter dropdowns correctly
      fetchActionPlansForFilters();
    }
  }, [selectedLocation?.displayText, fetchActionPlansForFilters]);

  // Initial data load - only depend on actual data values, not on callback functions
  // Note: calendarDate is NOT in dependencies because navigation changes are handled by handleCalendarNavigationChange
  // Note: fetchActionPlansForFilters is called separately in the location change effect above
  // Skip calendar fetch in standup context to avoid duplicate API requests
  useEffect(() => {
    if (!skipCalendarAndFilters) {
      fetchCalendarEvents(calendarDate, calendarViewMode);
    }
    fetchActionPlans(1, {
      location: selectedLocation?.displayText || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.displayText, activeTab, skipCalendarAndFilters]);

  // Handler functions
  const handleTabChange = (tab: 'Hybrid' | 'Table' | 'Calendar') => {
    setActiveTab(tab);
    console.log('Tab changed to:', tab);
  };

  const handlePageChange = (page: number) => {
    console.log('Page changed to:', page);
    setCurrentPage(page);
    const newFilters = { ...filters, pageNumber: page };
    fetchActionPlans(page, newFilters);
  };

  const handlePageSizeChange = (pageSize: number) => {
    console.log('Page size changed to:', pageSize);
    setCurrentPage(1);
    const newFilters = {
      ...filters,
      pageSize,
      pageNumber: 1
    };
    setFilters(newFilters);
    fetchActionPlans(1, newFilters);
  };

  const handleRetry = () => {
    fetchActionPlans(currentPage);
  };

  const handleCalendarRetry = () => {
    fetchCalendarEvents(calendarDate, calendarViewMode);
  };

  const handleCalendarNavigationChange = useCallback(
    (newDate: Date, viewMode: 'weekly' | 'monthly') => {
      setCalendarDate(newDate);
      setCalendarViewMode(viewMode);
      fetchCalendarEvents(newDate, viewMode);
    },
    [fetchCalendarEvents]
  );

  const handleDateSelect = (date: Date) => {
    console.log('Selected date:', date.toLocaleDateString());
  };

  const handleEventPress = (event: CalendarEvent) => {
    console.log('Event pressed:', event.title);
  };

  const handleEventRemove = (eventId: string) => {
    console.log('Remove event:', eventId);
    setEvents((prev) => prev.filter((e) => e.id !== eventId));
  };

  const handleEventDetailsPress = (event: CalendarEvent) => {
    console.log('Event details pressed:', event.title);
  };

  const handleShowAllEvents = (date: Date, eventsForDate: CalendarEvent[]) => {
    console.log('Show all events for', date.toLocaleDateString(), ':', eventsForDate.length, 'events');
  };

  const handleEditActionPlan = (item: ActionPlanItem) => {
    setSelectedActionPlanId(String(item.ID));
    setSelectedActionPlanData(item);
    setIsEditMode(true);
    setShowActionPlanDetails(true);
    console.log('Navigating to ActionPlanDetails with ID:', item.ID, 'in edit mode');
  };

  const handleViewActionPlanDetails = (item: ActionPlanItem) => {
    setSelectedActionPlanId(String(item.ID));
    setSelectedActionPlanData(item);
    setIsEditMode(false);
    setShowActionPlanDetails(true);
    console.log('Navigating to ActionPlanDetails with ID:', item.ID, 'in view mode');
  };

  const handleCalendarEventViewDetails = (event: CalendarEvent) => {
    if (event.actionPlanData) {
      setSelectedActionPlanId(String(event.actionPlanData.ID));
      setSelectedActionPlanData(event.actionPlanData);
      setShowActionPlanDetails(true);
      console.log('Navigating to ActionPlanDetails with event ID:', event.actionPlanData.ID);
    }
  };

  const handleBackFromDetails = () => {
    setShowActionPlanDetails(false);
    setSelectedActionPlanId(null);
    setSelectedActionPlanData(null);
    setIsEditMode(false);
  };

  const toggleFiltersExpansion = useCallback(() => {
    const toValue = isFiltersExpanded ? 0 : 1;
    setIsFiltersExpanded(!isFiltersExpanded);

    Animated.timing(filterAnimation, {
      toValue,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [isFiltersExpanded, filterAnimation]);

  const getResponsiveItemsPerRow = useCallback((itemsPerRow: number): number => {
    const windowWidth = Dimensions.get('window').width;

    if (Platform.OS === 'web') {
      return windowWidth > 1200
        ? Math.min(itemsPerRow, 4)
        : Math.min(itemsPerRow, 3);
    } else if (Platform.OS === 'ios' || Platform.OS === 'android') {
      if (isTablet) {
        return Math.min(itemsPerRow, 3);
      }
      return Math.min(itemsPerRow, 3);
    }
    return itemsPerRow;
  }, [isTablet]);

  const handleCalendarViewModeChange = useCallback(
    (mode: 'weekly' | 'monthly') => {
      setCalendarViewMode(mode);
      fetchCalendarEvents(calendarDate, mode);
    },
    [calendarDate, fetchCalendarEvents]
  );

  return {
    // State
    activeTab,
    actionPlanData,
    isLoading,
    error,
    pagination,
    currentPage,
    filters,
    filterOptions,
    events,
    calendarEventsLoading,
    calendarEventsError,
    calendarDate,
    isFiltersExpanded,
    filterAnimation,
    showActionPlanDetails,
    selectedActionPlanId,
    selectedActionPlanData,
    isEditMode,
    isMobile,
    isTablet,

    // Handlers
    handleTabChange,
    handleFilterChange,
    handleStatusChange,
    handleDeleteSuccess,
    handlePageChange,
    handlePageSizeChange,
    handleRetry,
    handleCalendarRetry,
    handleCalendarNavigationChange,
    toggleFiltersExpansion,
    handleEditActionPlan,
    handleViewActionPlanDetails,
    handleCalendarEventViewDetails,
    handleBackFromDetails,
    resetFilters,
    getResponsiveItemsPerRow,
    handleCalendarViewModeChange,

    // Calendar event handlers
    handleDateSelect,
    handleEventPress,
    handleEventRemove,
    handleEventDetailsPress,
    handleShowAllEvents,

    // Setters for parent component to update list
    setActionPlanData,
    setSelectedActionPlanData,
    setFilterDataset,
  };
};
